import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../Environments/Environment";
import { TestimonyFollowupRequest, TestimonySearchRequest } from "../Dto/TestimonySearchRequest";
import { TestimonyFollowupResponse, TestimonySearchResponse } from "../Dto/TestimonySearchResponse";
import { TestimonyReportEntity } from "../Dto/TestimonyModel";
import { TestimonyUpdateRequest } from "../Dto/TestimonyUpdateRequest";
import { TestimonyStatusUpdateRequest } from "../Dto/TestimonyStatusUpdateRequest";

@Injectable({ providedIn: "root" })
export class TestimonyApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchFollowups(params: TestimonyFollowupRequest): Promise<TestimonyFollowupResponse> {
    const url = this.buildUrl("testimonies/followups", params);
    return await firstValueFrom(this.http.get<TestimonyFollowupResponse>(url, { withCredentials: true }));
  }

  async searchReports(params: TestimonySearchRequest): Promise<TestimonySearchResponse> {
    const url = this.buildUrl("testimonies", params);
    return await firstValueFrom(this.http.get<TestimonySearchResponse>(url, { withCredentials: true }));
  }

  async getReport(id: string): Promise<TestimonyReportEntity> {
    const url = new URL(`testimonies/${id}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.get<TestimonyReportEntity>(url.toString(), { withCredentials: true }));
  }

  async createFromRequest(requestId: string): Promise<TestimonyReportEntity> {
    const url = new URL(`testimonies/from-request/${requestId}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.post<TestimonyReportEntity>(url.toString(), {}, { withCredentials: true }));
  }

  async updateReport(id: string, payload: TestimonyUpdateRequest): Promise<TestimonyReportEntity> {
    const url = new URL(`testimonies/${id}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.put<TestimonyReportEntity>(url.toString(), payload, { withCredentials: true }));
  }

  async updateStatus(id: string, payload: TestimonyStatusUpdateRequest): Promise<TestimonyReportEntity> {
    const url = new URL(`testimonies/${id}/status`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.put<TestimonyReportEntity>(url.toString(), payload, { withCredentials: true }));
  }

  async submitReport(id: string): Promise<TestimonyReportEntity> {
    const url = new URL(`testimonies/${id}/submit`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.post<TestimonyReportEntity>(url.toString(), {}, { withCredentials: true }));
  }

  async deleteReport(id: string): Promise<void> {
    const url = new URL(`testimonies/${id}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  private buildUrl(path: string, params: object): string {
    const url = new URL(path, `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();
    Object.entries(params as Record<string, string | number | undefined>).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        searchParams.set(key, value.toString());
      }
    });
    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }
    return url.toString();
  }
}
